import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import { JoinService } from '../../../services/join/join.service';
import { UserDataService } from '../../../services/user-data/user-data.service';
import { FunnelGuardSyncService } from '../../../services/driver/funnel-guard-sync.service';
import { environment } from '../../../../../environments/environment';
import { DriverService } from '../../../services/driver/driver.service';
import { ApiDriverInterfaceService } from '../../../services/api-interface/api-driver-interface';
import { RoutePaths } from '../../../../shared/constants/route-paths.constant';
import { isReferredByUberPartner } from '../../../../shared/utitlity-functions/utility-functions';
import {
    UBER_REAUTH_EXCLUDED_STATUSES,
    getUberReauthMessage,
} from '../../../../shared/constants/driver-status.constant';

@Component({
    standalone: false,
    selector: 'octo-uber-connect',
    templateUrl: './uber-connect.component.html',
    styleUrls: ['./uber-connect.component.scss'],
})
export class UberConnectComponent implements OnInit, OnDestroy {
    @Input() page: string = 'otp';

    userData$ = new Subscription();
    status: any;
    userSummary: any;
    uberLink: string = environment.UBER_LINK;
    isNotUberConnected: boolean = false;
    isAdmin: boolean = false;
    isGuardLoading: boolean = true;
    isReferredByUberPartner: boolean = false;
    needsUberReauth: boolean = false;
    isReauthChecking: boolean = true;
    private reauth$ = new Subscription();

    constructor(
        private readonly joinService: JoinService,
        private readonly router: Router,
        private readonly funnelGuardSyncService: FunnelGuardSyncService,
        private readonly authService: AuthenticationService,
        private readonly userDataService: UserDataService,
        private readonly driverService: DriverService,
        private readonly apiDriverInterfaceService: ApiDriverInterfaceService,
        private readonly cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.isAdmin = this.authService.isAdmin();

        // For join/signup flow, check if data is already cached in the service
        if (this.page !== 'my-account' && this.joinService.userSummaryDetails) {
            this.setUserData(this.joinService.userSummaryDetails);
        }

        const userDataObservable$ = this.joinService.getUserData();
        userDataObservable$.subscribe((data: any) => {
            this.setUserData(data);
            this.isGuardLoading = false;
        });

        // Read directly rather than via UserDataService.fetchUserSummary(), which also overwrites
        // localStorage status and the tablet_id cookie — unwanted side effects mid-funnel.
        this.reauth$ = this.apiDriverInterfaceService.get('driver/summary/', null).subscribe({
            next: (response: any) => {
                this.needsUberReauth =
                    !!response?.data?.is_uber_reauth_needed &&
                    !UBER_REAUTH_EXCLUDED_STATUSES.includes(response?.data?.status);
                this.isReauthChecking = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.isReauthChecking = false;
                this.cdr.detectChanges();
            },
        });
    }

    ngOnDestroy(): void {
        this.reauth$.unsubscribe();
    }

    setUserData(data: any): void {
        this.status = data?.status;
        if (!this.status) this.status = localStorage.getItem('status');
        this.userSummary = {
            texted_logs: data?.texted_logs,
            uber_connect: data?.uber_connect,
            referred_by: data?.referral_info?.referred_by,
            allowUberConnection: data?.allow_uber_connection,
            isReferredByUberPartner: isReferredByUberPartner(data?.referral_info?.referred_by),
        };
        // Check if user is allowed to connect AND is not already connected AND is not an admin
        if (
            (this.userSummary?.allowUberConnection) && !this.isAdmin
        ) {
            this.isNotUberConnected = true;

        }
    }

    get uberReauthMessage(): string {
        return getUberReauthMessage(this.status);
    }

    gotoCurrentPage(): void {

        // Update backend that user has skipped Uber connection
        this.driverService.skipUberConnection().subscribe({
            next: () => {
                // Navigate to appropriate page
                this.router.navigate([RoutePaths.ROOT_PATH]);

                this.funnelGuardSyncService.setFunnelGuardSync(false);
            },
            error: (error) => {

                // Still navigate to appropriate page even if error occurs
                this.router.navigate([RoutePaths.ROOT_PATH]);

                this.funnelGuardSyncService.setFunnelGuardSync(false);
            }
        });
    }

    linkAccount() {
        // Create an encrypted string that contains the current page
        let stateDict = {
            sourcePage: globalThis.location.pathname.split('/').pop(),
            isApplication: [
                'tos_complete',
                'driver_info_complete',
                'profile_complete',
                'payment_complete',
            ].includes(localStorage.getItem('status') || ''),
            isReferredByUberPartner: this.userSummary?.isReferredByUberPartner || false,
            is_reauth: this.needsUberReauth,
        };
        const state = btoa(JSON.stringify(stateDict));

        // Go to the Uber link
        const nextUrl = encodeURIComponent(`${this.uberLink}&state=${state}`);
        globalThis.open(`${environment.UBER_LOGOUT_URL}?next_url=${nextUrl}`, '_self');
    }
}
